// src/App.js
import React, { useState, useEffect, useRef } from 'react';
import {
  BrowserRouter,
  Routes,
  Route,
  Navigate,
  Outlet,
  useNavigate,
  useParams,
  useLocation,
  Link
} from 'react-router-dom';
import './App.css';
import { api, api_noIntercept } from './core/api/axios';
import { AuthContextProvider, useAuth } from './core/auth/AuthContext';
import { applyBrandingFromObject } from './core/auth/AuthContext';
import { getHeaderAccessToken } from './core/api/headerTokens';

import { applyHeaderTokensFromResponse } from './core/api/headerTokens';
import { setAutoLoginFlag, clearAutoLoginFlag, setAutoLoginErrorMessage } from './core/auth/AuthContext';
import { ShoppingCartProvider } from "./apps/customer_operations/ShoppingCartContext";
import ShoppingCartModal from "./apps/customer_operations/ShoppingCartModal";

import defaultLogo from './assets/icons/Logo iAnalytics.png';
import PortalHeadManager from './core/layout/PortalHeadManager';
import { lazyComponent } from './core/utils/lazyComponent';

// Pagina's worden pas geladen als ze geopend worden (zie core/utils/lazyComponent.js).
const Main = lazyComponent(() => import('./core/layout/Main'));
const MyProfile = lazyComponent(() => import('./core/account/MyProfile'));
const MyCompanies = lazyComponent(() => import('./core/account/My-companies'));
const Register = lazyComponent(() => import('./core/auth/RegisterPage'));
const AddReport = lazyComponent(() => import('./apps/operational_insights/AddReport'));
const ConfigCompany = lazyComponent(() => import('./core/company/ConfigCompany'));
const ConfigCompanyReport = lazyComponent(() => import('./apps/operational_insights/ConfigCompanyReport'));
const ConfigCompanyUser = lazyComponent(() => import('./core/company/ConfigCompanyUser'));
const AcceptInvitePage = lazyComponent(() => import('./core/auth/AcceptInvite'));
const NetworkInvitePage = lazyComponent(() => import('./core/auth/NetworkInvitePage'));
const ResetPassword = lazyComponent(() => import('./core/auth/ResetPassword'));
const StandaloneForm = lazyComponent(() => import('./core/forms/StandaloneForm'));
const QrLogin = lazyComponent(() => import('./core/auth/QrLogin'));
const StandaloneFormQR = lazyComponent(() => import('./core/forms/StandaloneFormQR'));
const IntegrationError = lazyComponent(() => import('./core/auth/IntegrationError'));
const ReportChat = lazyComponent(() => import('./apps/operational_insights/ReportChat'));



function RedirectToLogin() {
  const navigate = useNavigate();
  useEffect(() => {
    navigate('/login');
  }, [navigate]);
  return null;
}

function PrivateRoute({ children }) {
  const { user } = useAuth();
  if (!user) {
    return <Navigate to="/login" />;
  }
  return children;
}

const Spinner = () => <div className="spinner"></div>;

function Login() {
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [loadingText, setLoadingText] = useState('Logging in...');
  const [showPopupForgot, setShowPopupForgot] = useState(false);
  const [showPrivacy, setShowPrivacy] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [showMessagePopup, setShowMessagePopup] = useState(false);
  const [message, setMessage] = useState('');
  const [showErrorPopup, setShowErrorPopup] = useState(false);
  const [show2FA, setShow2FA] = useState(false);
  const [otp, setOtp] = useState('');
  const [mfaToken, setMfaToken] = useState(null);
  const [logoPath, setLogoPath] = useState(null);
  const [logoLoading, setLogoLoading] = useState(true);
  const [keepMeSignedIn, setKeepMeSignedIn] = useState(false);

  const formRef = useRef();
  const navigate = useNavigate();
  const { companyId } = useParams();
  const { login, completeMfaLogin } = useAuth();

  const [privacyHtml, setPrivacyHtml] = useState('');
  const [privacyLoading, setPrivacyLoading] = useState(false);
  const [privacyError, setPrivacyError] = useState('');

  const S3_BASE =
    process.env.REACT_APP_S3_BASE_URL ||
    'https://s3-ianalytics.s3.eu-west-3.amazonaws.com';

  const fileExts = ['png', 'jpg', 'jpeg', 'gif', 'bmp'];

  const [uiReady, setUiReady] = useState(false);

  const EyeIcon = () => (
    <svg
      className="ia-password-toggle-svg"
      viewBox="0 0 24 24"
      aria-hidden="true"
      focusable="false"
    >
      <path
        d="M2.25 12s3.75-6.75 9.75-6.75S21.75 12 21.75 12 18 18.75 12 18.75 2.25 12 2.25 12Z"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <circle
        cx="12"
        cy="12"
        r="3"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
      />
    </svg>
  );

  const EyeOffIcon = () => (
    <svg
      className="ia-password-toggle-svg"
      viewBox="0 0 24 24"
      aria-hidden="true"
      focusable="false"
    >
      <path
        d="M3 3l18 18"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
      />
      <path
        d="M2.25 12s3.75-6.75 9.75-6.75c1.64 0 3.1.5 4.35 1.22M21.75 12S18 18.75 12 18.75c-1.63 0-3.08-.49-4.32-1.2"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M9.88 9.88A3 3 0 0 0 14.12 14.12"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
      />
    </svg>
  );

  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        if (!companyId) {
          applyBrandingFromObject({ primary: '#FFFFFF', secondary: '#373187' });
          if (!cancelled) setUiReady(true);
          return;
        }

        const { data } = await api.get('/public/company-branding', {
          params: { companyId },
        });

        if (data?.success && data?.branding) {
          applyBrandingFromObject(data.branding);
        } else {
          applyBrandingFromObject({ primary: '#FFFFFF', secondary: '#373187' });
        }

        if (!cancelled) setUiReady(true);
      } catch {
        applyBrandingFromObject({ primary: '#FFFFFF', secondary: '#373187' });
        if (!cancelled) setUiReady(true);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [companyId]);

  useEffect(() => {
    if (!showPrivacy) return;

    let cancelled = false;

    (async () => {
      try {
        setPrivacyLoading(true);
        setPrivacyError('');

        const { data } = await api.get('/api/public/legal/privacy', {
          params: companyId ? { companyId } : {},
        });

        const html = data?.privacy?.content_html || '<p>Not available.</p>';

        if (!cancelled) setPrivacyHtml(html);
      } catch (e) {
        if (!cancelled) setPrivacyError('Privacy statement not available.');
      } finally {
        if (!cancelled) setPrivacyLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [showPrivacy, companyId]);

  useEffect(() => {
    let cancelled = false;

    const tryLoad = (src) =>
      new Promise((resolve) => {
        const img = new Image();
        img.onload = () => resolve(true);
        img.onerror = () => resolve(false);
        img.src = src;
      });

    (async () => {
      setLogoLoading(true);

      if (!companyId) {
        if (!cancelled) {
          setLogoPath(defaultLogo);
          setLogoLoading(false);
        }
        return;
      }

      const t = Date.now();
      let foundUrl = null;

      for (const ext of fileExts) {
        const url = `${S3_BASE}/Media/Company%20Logos/Logo-${companyId}.${ext}?t=${t}`;
        // eslint-disable-next-line no-await-in-loop
        const ok = await tryLoad(url);
        if (ok) {
          foundUrl = url;
          break;
        }
      }

      if (!cancelled) {
        setLogoPath(foundUrl || defaultLogo);
        setLogoLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [companyId, S3_BASE]);

  useEffect(() => {
    if (!isLoading) return;

    const t1 = setTimeout(() => setLoadingText('Loading user data...'), 2500);
    const t2 = setTimeout(() => {
      const redirect = sessionStorage.getItem('redirectAfterLogin');
      sessionStorage.removeItem('redirectAfterLogin');
      navigate(redirect || '/main');
    }, 5000);

    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
    };
  }, [isLoading, navigate]);

  const showErrorMessage = (txt, ms = 3000) => {
    setError(txt);
    setShowErrorPopup(true);
    setTimeout(() => setShowErrorPopup(false), ms);
  };

  const showSuccessMessage = (txt, ms = 3000) => {
    setMessage(txt);
    setShowMessagePopup(true);
    setTimeout(() => setShowMessagePopup(false), ms);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const data = new FormData(formRef.current);
    const email = data.get('email');
    const password = data.get('password');

    try {
      const userData = await login(email, password, keepMeSignedIn);

      if (userData.mfa_required) {
        setMfaToken(userData.mfa_token);
        setOtp('');
        setShow2FA(true);
      } else {
        setIsLoading(true);
      }
    } catch (err) {
      showErrorMessage(err.response?.status === 429
        ? 'Too many sign-in attempts. Please wait a few minutes and try again.'
        : 'Invalid email or password');
    }
  };

  const verifyMFA = async () => {
    try {
      await completeMfaLogin(mfaToken, otp);
      setMfaToken(null);
      setShow2FA(false);
      setIsLoading(true);
    } catch (err) {
      if (err.response?.status === 401) {
        // challenge expired or used up: start again with e-mail and password
        setMfaToken(null);
        setShow2FA(false);
        showErrorMessage(err.response?.data?.error || 'Please sign in again.', 5000);
        return;
      }

      showErrorMessage(err.response?.data?.error || 'Error verifying OTP');
    }
  };

  const handleForgot = async () => {
    try {
      const resp = await api.post('/api/login-forgot-password', {
        action: 'forgot_password',
        email_forgot_password: forgotEmail,
        company_id: companyId || null,
        portal_host: window.location.hostname,
      });

      if (resp.data.success) {
        setShowPopupForgot(false);
        setForgotEmail('');
        showSuccessMessage('Instructions sent');
      }
    } catch {
      showErrorMessage('Error sending instructions');
    }
  };

  const closeForgotModal = () => {
    setShowPopupForgot(false);
    setForgotEmail('');
  };

  const close2FAModal = () => {
    setShow2FA(false);
    setOtp('');
  };

  if (isLoading) {
    return (
      <div className="ia-login-loading-page">
        <div className="ia-login-loading-card">
          <h2 className="loading-h2">{loadingText}</h2>
          <Spinner />
          <p>Preparing the platform...</p>
        </div>
      </div>
    );
  }

  if (!uiReady) {
    return (
      <div className="ia-login-loading-page">
        <div className="ia-login-loading-card compact">
          <Spinner />
        </div>
      </div>
    );
  }

  if (logoLoading) {
    return (
      <div className="ia-login-loading-page">
        <div className="ia-login-loading-card compact">
          <Spinner />
        </div>
      </div>
    );
  }

  return (
    <div className="login-container ia-login-page">
      <div className="ia-login-shell ia-login-shell--single">
        <section className="login-panel ia-login-card">
          {logoPath && (
            <div className="ia-login-logo-wrap">
              <img
                src={logoPath}
                alt="Company logo"
                className="login-logo ia-login-logo"
                onError={() => setLogoPath(defaultLogo)}
              />
            </div>
          )}

          <div className="ia-login-heading">
            <h2>Sign in</h2>
            <p>Use your account to continue.</p>
          </div>

          <form ref={formRef} className="login-form ia-login-form" onSubmit={handleSubmit}>
            <div className="input-wrapper ia-login-field">
              <label htmlFor="email">E-mail address</label>
              <input
                type="email"
                name="email"
                id="email"
                autoComplete="email"
                placeholder="name@company.com"
                required
              />
            </div>

            <div className="input-wrapper ia-login-field">
              <label htmlFor="password">Password</label>

              <div className="password-input ia-password-input">
                <input
                  type={showPassword ? 'text' : 'password'}
                  id="password"
                  name="password"
                  autoComplete="current-password"
                  placeholder="Your password"
                  required
                />

                <button
                  type="button"
                  className="show-hide-button ia-password-toggle"
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                  onClick={() => setShowPassword((p) => !p)}
                >
                  {showPassword ? <EyeOffIcon /> : <EyeIcon />}
                </button>
              </div>
            </div>

            <div className="remember-forgot ia-login-options">
              <label className="remember-me ia-remember-me" htmlFor="keepMeSignedInCheckbox">
                <input
                  type="checkbox"
                  id="keepMeSignedInCheckbox"
                  checked={keepMeSignedIn}
                  onChange={(e) => setKeepMeSignedIn(e.target.checked)}
                />
                <span>Keep me signed in</span>
              </label>

              <button
                type="button"
                className="link-button ia-login-link"
                onClick={() => {
                  setForgotEmail('');
                  setShowPopupForgot(true);
                }}
              >
                Forgot password?
              </button>
            </div>

            <button type="submit" className="login-button ia-login-submit">
              Log in
            </button>

            <div className="or-separator ia-login-separator">
              <div className="line" />
              <div className="or-text">OR</div>
              <div className="line" />
            </div>

            <div className="create-account-privacy ia-login-footer-links">
              {!companyId && (
                <>
                  <Link to="/register" className="link-button ia-login-link">
                    Create account
                  </Link>

                  <span className="dot">·</span>
                </>
              )}

              <button
                type="button"
                className="link-button ia-login-link"
                onClick={() => setShowPrivacy(true)}
              >
                Privacy statement
              </button>
            </div>
          </form>
        </section>
      </div>

      {showPopupForgot && (
        <div className="ia-login-modal-backdrop" role="dialog" aria-modal="true">
          <div className="ia-login-modal">
            <div className="ia-login-modal-header">
              <h2>Forgot password?</h2>
              <p>Enter your e-mail address and we will send password reset instructions.</p>
            </div>

            <form
              className="ia-login-modal-body"
              onSubmit={(event) => {
                event.preventDefault();
                if (forgotEmail.trim()) handleForgot();
              }}
            >
              <div className="ia-login-field">
                <label htmlFor="forgotEmail">E-mail address</label>
                <input
                  id="forgotEmail"
                  type="email"
                  placeholder="name@company.com"
                  value={forgotEmail}
                  onChange={(e) => setForgotEmail(e.target.value)}
                  autoComplete="email"
                  required
                />
              </div>

              <div className="ia-login-modal-footer">
                <button
                  className="ia-login-modal-button"
                  type="button"
                  onClick={closeForgotModal}
                >
                  Cancel
                </button>

                <button
                  className="ia-login-modal-button primary"
                  type="submit"
                  disabled={!forgotEmail.trim()}
                >
                  Send instructions
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showPrivacy && (
        <div className="ia-login-modal-backdrop" role="dialog" aria-modal="true">
          <div className="ia-login-modal ia-login-modal-large">
            <div className="ia-login-modal-header">
              <h2>Privacy statement</h2>
              <p>Read how your data is handled in this platform.</p>
            </div>

            <div className="ia-login-modal-body">
              <div className="ia-privacy-scroll">
                {privacyLoading ? (
                  <div className="ia-login-inline-loader">
                    <Spinner />
                  </div>
                ) : privacyError ? (
                  <div className="ia-login-modal-error">{privacyError}</div>
                ) : (
                  <div dangerouslySetInnerHTML={{ __html: privacyHtml }} />
                )}
              </div>
            </div>

            <div className="ia-login-modal-footer">
              <button
                className="ia-login-modal-button primary"
                type="button"
                onClick={() => setShowPrivacy(false)}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {show2FA && (
        <div className="ia-login-modal-backdrop" role="dialog" aria-modal="true">
          <div className="ia-login-modal">
            <div className="ia-login-modal-header">
              <h2>Two-factor authentication</h2>
              <p>Enter the one-time password from your authenticator app.</p>
            </div>

            <form
              className="ia-login-modal-body"
              onSubmit={(event) => {
                event.preventDefault();
                if (otp.trim()) verifyMFA();
              }}
            >
              <div className="ia-login-field">
                <label htmlFor="OTP_Code">One-time password</label>
                <input
                  type="text"
                  id="OTP_Code"
                  name="OTP_Code"
                  placeholder="One-time password"
                  autoComplete="one-time-code"
                  value={otp}
                  onChange={(e) => setOtp(e.target.value)}
                  required
                />
              </div>

              <div className="ia-login-modal-footer">
                <button
                  className="ia-login-modal-button"
                  type="button"
                  onClick={close2FAModal}
                >
                  Cancel
                </button>

                <button
                  className="ia-login-modal-button primary"
                  type="submit"
                  disabled={!otp.trim()}
                >
                  Login
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showErrorPopup && <div className="popup ia-login-toast-error">{error}</div>}
      {showMessagePopup && <div className="popup-message ia-login-toast-success">{message}</div>}
    </div>
  );
}

function AutoLogin() {
  const { search }     = useLocation();
  const params         = new URLSearchParams(search);
  const token          = params.get('token');
  const appParam       = params.get('app');

  // ⬇️ nieuw: deep-link parameters uit IXON
  const deeplinkModuleId = params.get('moduleId');
  const deeplinkHideMenu = params.get('hideMenu'); // "1" of "0"

  const navigate       = useNavigate();
  const calledRef      = useRef(false);
  const { updateUser, setUserFromMe } = useAuth();

  useEffect(() => {
    if (!token || calledRef.current) return;
    calledRef.current = true;

    // Markeer deze sessie als AutoLogin (fallback, nog vóór user beschikbaar is)
    setAutoLoginFlag();

    api_noIntercept.post('/auto-login', { token })
      .then(async ({ data }) => {
        applyHeaderTokensFromResponse(data);

        // Forceer de Authorization header meteen voor alle volgende requests
        const access = getHeaderAccessToken();
        if (access) {
          api.defaults.headers.common['Authorization'] = `Bearer ${access}`;
          api_noIntercept.defaults.headers.common['Authorization'] = `Bearer ${access}`;
        }

        updateUser(data.user);
        const me = await setUserFromMe();  // ← bevat company_roleId

        // zet (indien meegegeven) de app in localStorage
        if (appParam) {
          window.localStorage.setItem('iA-App', appParam);
        }

        // ✅ Deeplink ALLEEN voor rollen > 3 (4,5,6)
        if (me?.company_roleId > 3) {
          if (deeplinkModuleId) {
            sessionStorage.setItem('ia_target_moduleId', String(deeplinkModuleId));
          }
          if (deeplinkHideMenu === '1' || deeplinkHideMenu === '0') {
            sessionStorage.setItem('ia_hideMenu', deeplinkHideMenu);
          }
        } else {
          // 🔇 Voor rollen ≤ 3: zorg dat er géén deeplink resten liggen
          sessionStorage.removeItem('ia_target_moduleId');
          sessionStorage.removeItem('ia_hideMenu');
        }

        // Opgeschoond: AutoLogin vlag mag weg zodra we succesvol zijn
        clearAutoLoginFlag();

        // én dan een harde redirect
        window.location.replace('/main');
      })
      .catch(() => {
        // Ongeldig token → neutrale foutpagina
        setAutoLoginErrorMessage('Auto-login failed: unknown or expired token.');
        navigate('/integration-error', { replace: true });
      });
  }, [token, navigate, updateUser]);

  return (
    <div style={{ padding: '2rem', textAlign: 'center' }}>
      <h3>One moment please… logging in automatically</h3>
    </div>
  );
}

function ProtectedLayout() {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return <Spinner />;
  }

  if (!user) {
    const lastCompanyId = sessionStorage.getItem('ia_last_company_id');

    return (
      <Navigate
        to={lastCompanyId ? `/login/${lastCompanyId}` : '/login'}
        replace
        state={{ from: location }}
      />
    );
  }

  return <Outlet />;
}

function PortalScopeManager() {
  const { pathname } = useLocation();

  useEffect(() => {
    const app = localStorage.getItem('iA-App');
    const isCustomerPortalContext = app === 'Customer Operations' && pathname === '/main';

    document.body.classList.toggle('customer-portal', isCustomerPortalContext);

    // Als je NIET in CP context zit: zorg dat page-kleur niet kan lekken
    if (!isCustomerPortalContext) {
      document.body.style.removeProperty('--body-bg');
      document.body.style.removeProperty('--detail-page-bg');
      document.body.style.removeProperty('--menu-bg');
      document.body.style.removeProperty('--card-bg');
    }
  }, [pathname]);

  return null;
}

function App() {
  return (
    <BrowserRouter>
      <PortalScopeManager />
      {/* ONE PROVIDER voor álle pagina’s, public én protected */}
      <AuthContextProvider>
        <PortalHeadManager />

        <ShoppingCartProvider>
          <Routes>
            {/* ─────── public ─────── */}
            <Route path="/" element={<RedirectToLogin />} />
            <Route path="/login" element={<Login />} />
            <Route path="/login/:companyId" element={<Login />} />
            <Route path="/auto-login" element={<AutoLogin />} />
            <Route path="/qr-login" element={<QrLogin />} />
            <Route path="/register" element={<Register />} />
            <Route path="/reset-password/:token" element={<ResetPassword />} />
            <Route path="/acceptinvite/:token/*" element={<AcceptInvitePage />} />
            <Route path="/network/invite" element={<NetworkInvitePage />} />
            <Route path="/integration-error" element={<IntegrationError />} />

            {/* Standalone routes authenticeren zichzelf via het externe token */}
            <Route path="/form" element={<StandaloneForm />} />
            <Route path="/form-qr" element={<StandaloneFormQR />} />

            {/* ─────── protected ─────── */}
            <Route element={<ProtectedLayout />}>
              <Route path="/main" element={<Main />} />
              <Route path="/my-profile" element={<MyProfile />} />
              <Route path="/my-companies" element={<MyCompanies />} />
              <Route path="/addreport" element={<AddReport />} />
              <Route path="/configcompany" element={<ConfigCompany />} />
              <Route path="/configcompanyreport/:reportId" element={<ConfigCompanyReport />} />
              <Route path="/configcompanyuser/:configCompanyId/:userId" element={<ConfigCompanyUser />} />
              <Route path="/report-chat" element={<ReportChat />} />

              {/* Protected fallback */}
              <Route path="*" element={<Navigate to="/main" replace />} />
            </Route>

            {/* Catch‐all voor echt onbestaande paden */}
            <Route path="*" element={<Navigate to="/login" replace />} />
          </Routes>

          {/* Globaal gemount, zodat de overlay/popup overal kan openen */}
          <ShoppingCartModal />
        </ShoppingCartProvider>
      </AuthContextProvider>
    </BrowserRouter>
  );
}

export default App;